import { router, usePage } from "@inertiajs/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SharedData } from "@/types";
import { urlFor } from "~/client";
import { useTransmitChannel } from "~/hooks/use-transmit-channel";
import { ARCADE_SECTION_ROUTE, arcadeDeepLinkPath } from "./battleship-matches";
import { useGameSounds } from "./use-game-sounds";

/**
 * App-wide realtime alert for the PvP arena. Subscribes to the current user's
 * PRIVATE workspace channel (`/:companyId/user/:userId/game-alert`, already
 * authorized against `user.id`) — the ONLY channel the target is subscribed to
 * before they open a match — and reacts to the minimal, viewer-agnostic payloads
 * broadcast by `app/events/game_alert_broadcast.ts`.
 *
 * On each alert it:
 *  - shows an in-app toast (foreground, with an action to jump to the arcade),
 *  - plays a distinctive Web Audio jingle IF the tab is active/unlocked and the
 *    user has not muted the arcade (best-effort; a never-focused background tab
 *    keeps its AudioContext suspended, so this is silent there — by design), and
 *  - raises a browser Notifications API notification when the tab is in the
 *    background AND permission is already granted (this is what actually alerts a
 *    backgrounded tab). It does NOT request permission here to avoid double
 *    prompting alongside the push-notifications flow.
 *
 * Game alerts ride a dedicated `game-alert` channel (NOT the notification-bell
 * channel), so the generic notification sound never fires for them — no
 * double-sound.
 *
 * Mount once in the authenticated layout.
 */

type GameAlertPayload =
  | { event: "game.challenge.received"; matchId: string; gameKey: string; fromName: string | null }
  | { event: "game.challenge.accepted"; matchId: string; gameKey: string; fromName: string | null }
  | { event: "game.match.found"; matchId: string; gameKey: string; fromName: string | null }
  | { event: "game.challenge.declined"; matchId: string; gameKey: string; fromName: string | null }
  | {
      event: "game.challenge.withdrawn";
      matchId: string;
      gameKey: string;
      fromName: string | null;
    };

function isGameAlertPayload(
  payload: GameAlertPayload | null | undefined,
): payload is GameAlertPayload {
  return (
    !!payload &&
    (payload.event === "game.challenge.received" ||
      payload.event === "game.challenge.accepted" ||
      payload.event === "game.match.found" ||
      payload.event === "game.challenge.declined" ||
      payload.event === "game.challenge.withdrawn")
  );
}

/**
 * Fire a background OS notification only when the tab is not visible and the user
 * already granted permission. Never throws, never prompts.
 */
function showBackgroundNotification(title: string, body: string, tag: string): void {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return;
  }
  if (Notification.permission !== "granted") {
    return;
  }
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    // Foreground is already covered by the toast + jingle; skip the OS toast.
    return;
  }
  try {
    const notification = new Notification(title, {
      body,
      icon: "/pwa-icons/icon-192.png",
      badge: "/pwa-icons/notification-badge.png",
      tag,
    });
    notification.addEventListener("click", () => {
      window.focus();
      notification.close();
    });
  } catch {
    // @expected-error-boundary: the OS notification is a best-effort background
    // hint; the toast/jingle already covered the foreground case.
  }
}

export function useGameChallengeAlert(): void {
  const { t } = useTranslation("chat");
  const { auth } = usePage<SharedData>().props;
  const companyId = auth.company?.id ? Number(auth.company.id) : null;
  const userId = auth.user?.id ? Number(auth.user.id) : null;
  const { play } = useGameSounds();

  const handleAlert = useCallback(
    (payload: GameAlertPayload) => {
      if (!isGameAlertPayload(payload)) {
        return;
      }

      const from = payload.fromName?.trim() || null;

      if (payload.event === "game.challenge.declined") {
        // No sound: a rejection should not be celebrated; just inform. Transient.
        toast.message(t("game_alerts.declined.title", "Rechazaron tu reto"), {
          description: from
            ? t("game_alerts.declined.body", {
                name: from,
                defaultValue: "{{name}} rechazó tu reto.",
              })
            : undefined,
        });
        return;
      }

      if (payload.event === "game.challenge.withdrawn") {
        // The challenger cancelled a challenge aimed at us: no sound, no action —
        // just clear the noise with a transient info toast. The incoming-challenge
        // screen itself clears via the match-channel reconcile.
        toast.message(t("game_alerts.withdrawn.title", "Reto cancelado"), {
          description: from
            ? t("game_alerts.withdrawn.body", {
                name: from,
                defaultValue: "{{name}} canceló el reto.",
              })
            : undefined,
        });
        return;
      }

      let title: string;
      let namedBody: string;
      if (payload.event === "game.challenge.received") {
        title = t("game_alerts.challenge.title", "Te retaron a jugar");
        namedBody = t("game_alerts.challenge.body", {
          name: from,
          defaultValue: "{{name}} quiere jugar contigo.",
        });
      } else if (payload.event === "game.challenge.accepted") {
        title = t("game_alerts.accepted.title", "Aceptaron tu reto");
        namedBody = t("game_alerts.accepted.body", {
          name: from,
          defaultValue: "{{name}} aceptó tu reto.",
        });
      } else {
        title = t("game_alerts.found.title", "¡Rival encontrado!");
        namedBody = t("game_alerts.found.body", {
          name: from,
          defaultValue: "{{name}} se unió a tu partida.",
        });
      }
      const body = from ? namedBody : t("game_alerts.generic.body", "Abre el arcade para jugar.");

      // Foreground reinforcement (only audible when this tab is active/unlocked).
      play("yourTurn");

      // Background alert (only when hidden AND permission already granted).
      showBackgroundNotification(title, body, `game:${payload.matchId}`);

      // In-app toast with a jump-to-arcade action. These three events
      // (received/accepted/found) are the ones that NEED an answer, so keep the
      // toast up until the user acts (`duration: Infinity`) — a missed transient
      // toast was why challenges went unanswered. The "Abrir" action deep-links to
      // the arcade with `?arcade=<gameKey>` so the RIGHT game's modal auto-opens on
      // the welcome screen (see `welcome-arcade-section.tsx`) — the alert carries the
      // game it belongs to, so a Battleship alert opens Battleship and a Wordle PvP
      // alert opens the Wordle race, never the wrong game. Once open, the prioritized
      // mine-adoption surfaces the incoming challenge / resumes play.
      const gameKey = payload.gameKey;
      toast.message(title, {
        description: body,
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: t("game_alerts.actions.open", "Abrir"),
          onClick: () => {
            // Same-page: the arcade section is already mounted, so a router.visit to
            // the same URL won't remount it (its mount-only param effect never
            // fires). Dispatch an event it listens for to open the modal in place.
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("arcade:open-game", { detail: { key: gameKey } }),
              );
            }
            // Cross-page: still carry the intent in the URL so the section opens the
            // modal on mount after navigation. Target the Internal channels welcome
            // screen — the ONLY page that mounts the arcade and reads `?arcade`;
            // `chat.index` renders a different page and would strand the user on the
            // external chat instead of the game. Carry the challenge's own gameKey so
            // the right game's modal opens (battleship or wordle_pvp).
            router.visit(arcadeDeepLinkPath(urlFor(ARCADE_SECTION_ROUTE), gameKey));
          },
        },
      });
    },
    [play, t],
  );

  const topic = companyId && userId ? `/${companyId}/user/${userId}/game-alert` : null;
  useTransmitChannel<GameAlertPayload>(topic, handleAlert);
}
